<script setup lang="ts">
import { computed, type PropType } from 'vue';
import { Icon } from '@iconify/vue';
import Button from 'primevue/button';
import Dialog from 'primevue/dialog';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import ColumnGroup from 'primevue/columngroup';
import Row from 'primevue/row';
import {formatedDate} from '@/constants'
// @ts-ignore
import html2pdf from 'html2pdf.js';
import type { YarnStockValueResponse } from '@/types/report-types';
import { CurrencyType } from '@/types/currency-types';

const props = defineProps({
  visible: { type: Boolean, required: true },
  onClose: { type: Function as PropType<() => void>, required: true },
  reportData: { type: Object as PropType<YarnStockValueResponse | null>, default: null },
  selectedDate: { type: Date as PropType<Date | null>, default: null },
  companyName: { type: String, default: 'Techknital Fabrics Ltd' },
  companyAddress: { type: String, default: 'Unit 8 - 9, Lime Kilns Business Park\nLime Kilns Way\nHinckley\nLeicestershire, LE10 #EL' },
  companyTel: { type: String, default: '+44 (0)1455 254350' },
  companyFax: { type: String, default: '+44 (0)1455 238998' },
});

const PDF_EXPORT_ELEMENT_ID = 'merge-stock-value-report-content';

const formattedDate = computed(() =>
    props.selectedDate ? formatedDate(props.selectedDate) : 'N/A'
);

const onDownloadReportPdf = () => {
  const reportElement = document.getElementById(PDF_EXPORT_ELEMENT_ID);
  if (!reportElement || !props.reportData) return;
  const filename = `yarn-stock-value-${props.selectedDate ? new Date(props.selectedDate).toISOString().split('T')[0] : 'na'}.pdf`;
  html2pdf().set({
    margin: [10, 5, 10, 5],
    filename,
    image: { type: 'jpeg', quality: 0.98 },
    html2canvas: { scale: 2, useCORS: true },
    jsPDF: { unit: 'mm', format: 'a4', orientation: 'landscape' }
  }).from(reportElement).save();
};

const hasReportData = computed(() => !!(props.reportData?.mergeStockValues?.length));

const formatCurrency = (value?: number, currencyId?: number) => {
  const currency = CurrencyType.value.find(c => c.id === currencyId) || CurrencyType.value[0];
  return typeof value === 'number' ? `${currency.symbol} ${value.toFixed(2)}` : `${currency.symbol} 0.00`;
};

const formatNumberTwoDecimals = (value?: number) => typeof value === 'number' ? value.toFixed(2) : '-';

// Get the primary currency for totals (assuming first currency or GBP)
const primaryCurrency = computed(() => CurrencyType.value[0]);

</script>

<template>
  <Dialog :visible="visible" modal :draggable="false" :style="{ width: '95rem', maxWidth: '98vw', minHeight: '90vh'}" :closable="false" @update:visible="onClose" class="report-dialog-custom-header">
    <template #header>
      <div class="flex justify-between items-center w-full">
        <Button link @click="onClose" aria-label="Back to report page" class="flex items-center gap-x-1 p-0 text-gray-600 hover:text-primary-default">
          <Icon icon="material-symbols:arrow-back-rounded" class="text-2xl md:text-3xl group-hover:-translate-x-1 transition-transform" />
          <span class="text-sm md:text-base hidden sm:inline">Back to Report Page</span>
        </Button>
        <div class="flex-grow text-center">
          <h2 class="text-lg md:text-xl font-semibold">Yarn Stock Value Report</h2>
        </div>
        <div class="flex items-center gap-x-2">
          <Button label="Download as PDF" @click="onDownloadReportPdf" :disabled="!hasReportData" class="p-button-danger text-sm md:text-base">
            <template #icon><Icon icon="material-symbols:download-2" class="text-xl ml-3 order-1" /></template>
          </Button>
        </div>
      </div>
    </template>

    <div class="dialog-main-content flex-grow overflow-y-auto">
      <div v-if="!reportData && visible" class="text-center py-10 text-gray-500">
        No data to display.
      </div>
      <div v-else-if="hasReportData" :id="PDF_EXPORT_ELEMENT_ID" class="p-2 md:p-4 bg-white">
        <div class="flex justify-between items-start mb-6">
          <div>
            <img src="@/assets/images/logo.png" alt="Company Logo" class="h-12 mb-1" />
          </div>
          <div class="text-right text-xs text-gray-500 whitespace-pre-line">
            <p>{{ companyAddress }}</p>
            <p>Tel: {{ companyTel }}</p>
            <p>Fax: {{ companyFax }}</p>
          </div>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6 p-4 bg-gray-50 rounded-md shadow-sm">
          <div>
            <h3 class="text-md font-semibold text-gray-700">Report Date</h3>
            <p class="text-sm text-gray-600">Date: {{ formattedDate }}</p>
          </div>
          <div class="md:text-right">
            <h3 class="text-md font-semibold text-gray-700">Yarn Stock Value Report</h3>
            <p class="text-sm text-gray-600">Total Items: {{ reportData?.mergeStockValues?.length || 0 }}</p>
          </div>
        </div>

        <div class="overflow-x-auto">
          <DataTable
              :value="reportData?.mergeStockValues"
              responsiveLayout="scroll"
              class="p-datatable-sm merge-stock-value-table min-w-full"
              stripedRows
              :rowHover="true"
              :scrollable="true"
              scrollHeight="60vh"
          >
            <Column field="mergeNumber" header="Merge Number" sortable :style="{ minWidth: '120px', whiteSpace: 'nowrap' }"></Column>
            <Column field="yarnType" header="Yarn Type" sortable :style="{ minWidth: '120px', whiteSpace: 'nowrap' }"></Column>
            <Column field="yarnColour" header="Yarn Colour" sortable :style="{ minWidth: '120px', whiteSpace: 'nowrap' }"></Column>
            <Column field="yarnCount" header="Yarn Count" sortable :style="{ minWidth: '100px', whiteSpace: 'nowrap' }"></Column>
            <Column field="kgAvailable" header="Kg Available" sortable :style="{ minWidth: '120px', whiteSpace: 'nowrap' }">
              <template #body="slotProps">{{ formatNumberTwoDecimals(slotProps.data.kgAvailable) }} kg</template>
            </Column>
            <Column field="costPerKg" header="Cost per Kg" sortable :style="{ minWidth: '120px', whiteSpace: 'nowrap' }">
              <template #body="slotProps">{{ formatCurrency(slotProps.data.costPerKg, slotProps.data.currency) }}</template>
            </Column>
            <Column field="totalValue" header="Total Value" sortable :style="{ minWidth: '120px', whiteSpace: 'nowrap' }">
              <template #body="slotProps">{{ formatCurrency(slotProps.data.totalValue, slotProps.data.currency) }}</template>
            </Column>

            <ColumnGroup type="footer" v-if="reportData && reportData.mergeStockValues.length > 0">
              <Row>
                <Column footer="Totals:" :colspan="4" footerStyle="text-align:left; font-weight: bold; color: #1f2937; padding-left: 0.5rem;" />
                <Column :footer="`${formatNumberTwoDecimals(reportData.totalKgInStock)} kg`" footerStyle="text-align:right; font-weight: bold; color: #1f2937;" />
                <Column footer="" footerStyle="text-align:right; font-weight: bold; color: #1f2937;" />
                <Column :footer="formatCurrency(reportData.totalStockValue, primaryCurrency.id)" footerStyle="text-align:right; font-weight: bold; color: #1f2937;" />
              </Row>
            </ColumnGroup>
          </DataTable>
        </div>

      </div>
      <div v-else-if="!hasReportData && visible" class="text-center py-10 text-gray-500">
        No Yarn stock value data found for the selected date.
      </div>
    </div>
  </Dialog>
</template>

<style scoped></style>
